import React, { useState, useMemo, useCallback, useContext } from "react"
import moment from "moment"
import { AD_TYPE_IDS } from "../../../constants/adTypes"
import truncate from "../../../utils/Truncate"
import "./AiTargeting.css"
import ButtonEdit from "../../ButtonEdit"
import InputEdit from "../../InputEdit"
import { useAiTargetingAdActions } from "./useAiTargetingAdActions"
import { GENDER_LABELS, AGE_RANGES } from "./aiTargetingConstants"
import UserContext from "../../../UserContext"
import { openResourcePreviewModal } from "../ResourcePreviewModal"

function DiffusionAiTargetingAdItem({
  aiTargetingAd,
  totemId,
  isDefaultAdRow,
  totemHasDefaultAd,
  otherTargetingAds = [],
  maxDuration,
  onDelete,
  onUpdate,
}) {
  const { ad, gender, ageRange, isDefault, planification, notVideoDuration } = aiTargetingAd
  const userContext = useContext(UserContext)

  const [editDuration, setEditDuration] = useState(false)
  const [newDuration, setNewDuration] = useState(notVideoDuration || 0)

  const onStartDurationEdit = useCallback(() => {
    setNewDuration(notVideoDuration || 0)
    setEditDuration(true)
  }, [notVideoDuration])

  const {
    handleEditTargeting,
    handleDurationEdit,
    handleDurationSubmit,
    handlePlanificationEdit,
    handleDelete,
  } = useAiTargetingAdActions({
    totemId,
    aiTargetingAd,
    isDefaultAdRow,
    totemHasDefaultAd,
    otherTargetingAds,
    maxDuration,
    onUpdate,
    onDelete,
    onStartDurationEdit,
    onDurationSubmitSuccess: () => setEditDuration(false),
  })

  const submitDuration = useCallback(() => {
    const value = typeof newDuration === "number" ? newDuration : parseInt(newDuration, 10)
    handleDurationSubmit(Number.isNaN(value) ? 0 : value)
  }, [newDuration, handleDurationSubmit])

  const isPlanificationActive = useMemo(() => {
    return planification && moment(planification.endDate).add(1, "day").isAfter(moment())
  }, [planification])

  const adDuration = ad?.duration || 0
  const totalDuration = adDuration + (notVideoDuration || 0)
  const isVideo = ad?.adTypeId === AD_TYPE_IDS.VIDEO

  const renderGenderBadge = () => {
    if (!gender) {
      return <span className="badge badge-secondary">Tous</span>
    }
    const genderInfo = GENDER_LABELS[gender]
    return (
      <span className={`badge badge-${genderInfo.color}`}>
        <i className={`${genderInfo.icon} mr-1`}></i>
        {genderInfo.label}
      </span>
    )
  }

  const sortedAgeRange = useMemo(() => {
    if (!ageRange || ageRange.length === 0) return []

    const orderIndex = AGE_RANGES.map((r) => r.value)

    return [...ageRange].sort((a, b) => orderIndex.indexOf(a) - orderIndex.indexOf(b))
  }, [ageRange])

  const renderAgeRanges = () => {
    if (sortedAgeRange.length === 0) {
      return <span className="text-muted">Tous</span>
    }
    if (sortedAgeRange.length > 3) {
      return (
        <span title={sortedAgeRange.join(", ")}>
          {sortedAgeRange.slice(0, 2).join(", ")}... <span className="badge badge-info">+{sortedAgeRange.length - 2}</span>
        </span>
      )
    }
    return sortedAgeRange.join(", ")
  }

  const renderDuration = () => {
    if (editDuration) {
      return (
        <InputEdit
          type="number"
          min="1"
          max={maxDuration ? maxDuration - adDuration : undefined}
          value={newDuration}
          onChange={(e) => setNewDuration(e.target.value)}
          onSubmit={submitDuration}
          onCancel={() => setEditDuration(false)}
        />
      )
    }
    return (
      <>
        {totalDuration} s
        {!isVideo ? <ButtonEdit onClick={handleDurationEdit} /> : null}
      </>
    )
  }

  const renderPlanification = () => {
    return (
      <>
        {planification ? (
          isPlanificationActive ? (
            <i className="far fa-calendar-check planification-active" title="Planification active"></i>
          ) : (
            <i
              className="far fa-calendar-times planification-inactive"
              title="Planification inactive, la ressource n'est plus diffusée"
            ></i>
          )
        ) : (
          <span className="text-muted">Non</span>
        )}
        <ButtonEdit onClick={handlePlanificationEdit} />
      </>
    )
  }

  return (
    <tr>
      <td>
        {isDefault ? (
          <i className="fas fa-star mr-2" style={{ color: "#d97706" }} title="Ressource par défaut"></i>
        ) : (
          <i className="fas fa-crosshairs mr-2" style={{ color: "#133D94" }} title="Ressource ciblée"></i>
        )}
        {truncate(ad?.name || "Ressource", 25)}
      </td>
      <td>{renderDuration()}</td>
      {!isDefault ? (
        <>
          <td>{renderPlanification()}</td>
          <td>{renderGenderBadge()}</td>
          <td>{renderAgeRanges()}</td>
        </>
      ) : null}
      <td className="project-actions text-right">
        <button
          className="btn btn-default btn-xs mr-1"
          onClick={() => openResourcePreviewModal(ad, userContext)}
          title="Prévisualiser"
        >
          <i className="far fa-eye"></i>
        </button>
        <button className="btn btn-info btn-xs mr-1" onClick={handleEditTargeting} title="Modifier le ciblage">
          <i className="fas fa-bullseye mr-1"></i>
          Modifier le ciblage
        </button>
        <button className="btn btn-danger btn-xs" onClick={handleDelete} title="Supprimer">
          <i className="fas fa-trash"></i>
        </button>
      </td>
    </tr>
  )
}

export default React.memo(DiffusionAiTargetingAdItem)
